import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

export interface User {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string; 
    postalCode?: string;
    isVerified: boolean;
    isAdmin: boolean;
    createdAt: Date;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<boolean>;
    register: (userData: RegisterData) => Promise<boolean>;
    logout: () => void;
    updateUser: (userData: Partial<User>) => Promise<boolean>;
}

interface RegisterData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    confirmPassword: string;
    address?: string;
    city?: string;
    postalCode?: string;
}


const getApiBaseUrl = (): string => {
    if (process.env.NODE_ENV === 'production') {
        return 'https://pccenter.bg';
    }

    return 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

logger.log(`Auth API URL: ${API_BASE_URL}`);

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000,
});

api.interceptors.request.use(
    async (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (process.env.NODE_ENV === 'development') {
            logger.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => {
        if (process.env.NODE_ENV === 'development') {
            logger.log(`API Response: ${response.status} ${response.config.url}`);
        }
        return response;
    },
    (error) => {
        if (process.env.NODE_ENV === 'development') {
            logger.error('API Error:', error.response?.status, error.response?.data);
        }
        
        if (error.response?.status === 401) {
            // Only redirect if we're not on login/register pages and not during a login attempt
            const isLoginAttempt = error.config?.url === '/auth/login';
            if (!isLoginAttempt) {
                localStorage.removeItem('authToken');
                if (window.location.pathname !== '/login' && 
                    window.location.pathname !== '/register' && 
                    window.location.pathname !== '/') {
                    window.location.href = '/login';
                }
            }
        }
        
        return Promise.reject(error);
    }
);

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const checkAuthStatus = async () => {
            try {
                const token = localStorage.getItem('authToken');
                if (!token) {
                    setIsLoading(false);
                    return;
                }

                const response = await api.get('/auth/profile');
                const userData = response.data;
                
                if (userData.createdAt) {
                    userData.createdAt = new Date(userData.createdAt);
                }
                
                setUser(userData);
            } catch (error: any) {
                logger.error('Грешка при проверка на автентикацията:', error);
                
                if (error.response?.status === 401 || !error.response) {
                    localStorage.removeItem('authToken');
                }
            } finally {
                setIsLoading(false);
            }
        };

        checkAuthStatus();
    }, []);

    const login = async (email: string, password: string): Promise<boolean> => {
        try {
            setIsLoading(true);

            const response = await api.post('/auth/login', {
                email,
                password
            });

            const { access_token, user: userData } = response.data;

            if (!access_token || !userData) {
                throw new Error('Невалиден отговор от сървъра');
            }

            localStorage.setItem('authToken', access_token);

            if (userData.createdAt) {
                userData.createdAt = new Date(userData.createdAt);
            }

            setUser(userData);
            return true;
        } catch (error: any) {
            logger.error('Грешка при влизане:', error);
            
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
                throw new Error('Невъзможна връзка със сървъра. Моля, опитайте по-късно.');
            } else if (error.response?.status === 401) {
                throw new Error('Невалиден имейл или парола');
            } else if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const register = async (userData: RegisterData): Promise<boolean> => {
        try {
            setIsLoading(true);

            // if (userData.password !== userData.confirmPassword) {
            //     throw new Error('Паролите не съвпадат');
            // }

            const { confirmPassword, ...registrationData } = userData;

            const cleanedData: any = {
                firstName: registrationData.firstName,
                lastName: registrationData.lastName,
                email: registrationData.email,
                phone: registrationData.phone,
                password: registrationData.password,
            };

            if (registrationData.address?.trim()) {
                cleanedData.address = registrationData.address.trim();
            }
            if (registrationData.city?.trim()) {
                cleanedData.city = registrationData.city.trim();
            }
            if (registrationData.postalCode?.trim()) {
                cleanedData.postalCode = registrationData.postalCode.trim();
            }

            // Log the request data for debugging
            logger.log('🚀 Registration request data:', {
                ...cleanedData,
                password: '[HIDDEN]' // Don't log the actual password
            });

            // Production-specific debugging
            if (process.env.NODE_ENV === 'production') {
                logger.log('🌐 Production environment details:', {
                    apiUrl: API_BASE_URL,
                    userAgent: navigator.userAgent,
                    origin: window.location.origin,
                    timestamp: new Date().toISOString()
                });
            }

            const response = await api.post('/auth/register', cleanedData);

            logger.log('✅ Registration successful:', {
                hasToken: !!response.data.access_token,
                hasUser: !!response.data.user,
                userEmail: response.data.user?.email
            });

            const { access_token, user: newUser } = response.data;

            if (!access_token || !newUser) {
                throw new Error('Невалиден отговор от сървъра');
            }

            localStorage.setItem('authToken', access_token);

            if (newUser.createdAt) {
                newUser.createdAt = new Date(newUser.createdAt);
            }

            setUser(newUser);
            return true;
        } catch (error: any) {
            logger.error('❌ Грешка при регистрация:', error);
            
            // Enhanced error logging
            if (error.response) {
                logger.error('📋 Response details:', {
                    status: error.response.status,
                    statusText: error.response.statusText,
                    data: error.response.data,
                    headers: error.response.headers
                });

                // Log validation errors specifically
                if (error.response.status === 400 && error.response.data) {
                    logger.error('🔍 Validation errors:', error.response.data);
                    
                    // If it's an array of validation messages
                    if (Array.isArray(error.response.data.message)) {
                        logger.error('📝 Individual validation errors:');
                        error.response.data.message.forEach((msg: string, index: number) => {
                            logger.error(`  ${index + 1}. ${msg}`);
                        });
                    }
                }
            } else if (error.request) {
                logger.error('📡 Request details:', {
                    url: error.config?.url,
                    method: error.config?.method,
                    timeout: error.code === 'ECONNABORTED',
                    networkError: error.code === 'ERR_NETWORK'
                });
            } else {
                logger.error('⚠️ Other error:', error.message);
            }
            
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
                throw new Error('Невъзможна връзка със сървъра. Моля, опитайте по-късно.');
            }
            
            // For HTTP errors, preserve the original error object so RegisterPage can handle it
            // Just re-throw the original error to maintain response status and data
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        try {
            await api.post('/auth/logout');
        } catch (error) {
            logger.error('Грешка при излизане:', error);
        } finally {
            localStorage.removeItem('authToken');
            setUser(null);
        }
    };

    const updateUser = async (userData: Partial<User>): Promise<boolean> => {
        try {
            setIsLoading(true);

            const response = await api.patch('/auth/profile', userData);
            const updatedUser = response.data;

            if (!updatedUser) {
                throw new Error('Невалиден отговор от сървъра');
            }

            if (updatedUser.createdAt) {
                updatedUser.createdAt = new Date(updatedUser.createdAt);
            }

            setUser(updatedUser);
            return true;
        } catch (error: any) {
            logger.error('Грешка при актуализиране на данните:', error);
            
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
                throw new Error('Невъзможна връзка със сървъра. Моля, опитайте по-късно.');
            } else if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        updateUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth трябва да се използва в AuthProvider');
    }
    return context;
};

export { api };